// src/components/features/Elevation/components/shared/ElevationRadioStep.jsx

import { Box, Flex, Text, Radio, Button, VStack } from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import SmartImage from "./SmartImage";

function ImageCarousel({ images, alt }) {
  const [index, setIndex] = useState(0);
  const startX = useRef(null);
  const [drag, setDrag] = useState(0);
  const timerRef = useRef(null);

  const total = images.length;

  const restartAutoPlay = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 2500);
  };

  useEffect(() => {
    if (total > 1) restartAutoPlay();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const getX = (e) => (e.touches ? e.touches[0]?.clientX : e.clientX);
  const THRESHOLD = 40;

  const onStart = (e) => {
    e.stopPropagation();
    startX.current = getX(e);
  };
  const onMove = (e) => {
    if (startX.current === null) return;
    e.stopPropagation();
    setDrag(getX(e) - startX.current);
  };
  const onEnd = (e) => {
    if (startX.current === null) return;
    e.stopPropagation();
    if (drag < -THRESHOLD) setIndex((i) => (i + 1) % total);
    else if (drag > THRESHOLD) setIndex((i) => (i - 1 + total) % total);
    setDrag(0);
    startX.current = null;
    restartAutoPlay();
  };

  return (
    <Box
      h="130px" position="relative" bg="gray.100" overflow="hidden"
      cursor="grab" userSelect="none"
      onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
      onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
      onClick={(e) => e.stopPropagation()}
    >
      <SmartImage src={images[index]} alt={alt} />

      {total > 1 && (
        <Flex position="absolute" bottom="6px" left={0} right={0} justify="center" gap="4px">
          {images.map((_, i) => (
            <Box
              key={i}
              w={i === index ? "14px" : "6px"} h="6px" borderRadius="full"
              bg={i === index ? "cyan.400" : "whiteAlpha.700"}
              transition="all 0.2s"
              boxShadow="0 0 2px rgba(0,0,0,0.4)"
            />
          ))}
        </Flex>
      )}
    </Box>
  );
}

// Props:
//   title, subtitle
//   options — [{ id, label, sub, img }]
//   formData, setFormData, onNext, onBack, isLastStep, onSubmit
export default function ElevationRadioStep({
  title, subtitle, options,
  fieldKey,
  formData, setFormData, onNext, onBack, isLastStep, onSubmit,
}) {
  const selected = formData?.[fieldKey] || "";

  const handleSelect = (id) => setFormData({ ...formData, [fieldKey]: id });

  const handleNext = () => {
    if (!selected) return;
    if (isLastStep) onSubmit(); else onNext();
  };

  return (
    <Box
      h="600px" w="100%" maxW="400px" mx="auto"
      display="flex" flexDirection="column" justifyContent="space-between"
      px={4} py={6}
    >
      <Box
        flex="1" minH="0" overflowY="auto" overflowX="hidden" pr={1}
        css={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" } }}
      >
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="17px" fontWeight="600" color="gray.800">{title}</Text>
            <Text fontSize="12px" color="gray.500" mt={0.5}>{subtitle}</Text>
          </Box>

          {options.map((opt) => {
            const isSelected = selected === opt.id;
            return (
              <Box
                key={opt.id}
                borderRadius="10px" overflow="hidden"
                border={isSelected ? "2px solid #319795" : "1px solid #E2E8F0"}
                cursor="pointer" transition="0.2s"
                _hover={{ borderColor: "cyan.400" }}
                onClick={() => handleSelect(opt.id)}
              >
                {opt.images?.length > 1 ? (
                  <ImageCarousel images={opt.images} alt={opt.label} />
                ) : opt.img || opt.images?.[0] ? (
                  <Box h="130px" position="relative" bg="gray.100">
                    <SmartImage src={opt.img || opt.images[0]} alt={opt.label} />
                  </Box>
                ) : null}
                <Flex align="center" px={3} py={2} bg={isSelected ? "cyan.50" : "white"}>
                  <Radio
                    value={opt.id} colorScheme="cyan" isChecked={isSelected}
                    onChange={() => handleSelect(opt.id)}
                  />
                  <Box ml={2}>
                    <Text fontSize="14px" fontWeight="600" color="gray.800">{opt.label}</Text>
                    {opt.sub && <Text fontSize="11px" color="gray.500">{opt.sub}</Text>}
                  </Box>
                </Flex>
              </Box>
            );
          })}
        </VStack>
      </Box>

      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button onClick={onBack} variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500" borderRadius="6px"
          minW="100px" h="40px" fontWeight="500" fontSize="14px" _hover={{ bg: "cyan.50" }}>
          ← Previous
        </Button>
        <Button onClick={handleNext} isDisabled={!selected} variant="solid"
          bg={selected ? "cyan.500" : "gray.300"} color="white"
          borderRadius="6px" minW="100px" h="40px" fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: selected ? "cyan.600" : "gray.300" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}>
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}